interface DropdownOption {
  label: string;
  value?: string | null;
  submitValue?: string | null;
}

/** Minimal shape of a form field list (top level or inside a fieldset). */
interface FieldLike {
  _type: string;
  name?: string;
  options?: DropdownOption[];
  fields?: FieldLike[];
}

/**
 * Swaps each dropdown's stored value for its option's `submitValue` (when it
 * has one) just before submission.
 *
 * A dropdown needs a unique value per option to work as a control (selection,
 * highlighting and keys all depend on it), but the system receiving the form
 * may use a coarser code that several options share. The option keeps its
 * unique value in the UI, and this translates it on the way out.
 */
export function applySubmitValues<T extends Record<string, unknown>>(
  fields: readonly FieldLike[],
  data: T
): T {
  const result: Record<string, unknown> = { ...data };

  const visit = (list: readonly FieldLike[]) => {
    for (const field of list) {
      if (field.fields) {
        visit(field.fields);
        continue;
      }
      if (field._type !== "dropdown" || !field.name || !field.options) continue;

      const stored = result[field.name];
      const match = field.options.find(
        (option) => (option.value ?? option.label) === stored
      );
      if (match?.submitValue) {
        result[field.name] = match.submitValue;
      }
    }
  };
  visit(fields);

  return result as T;
}
